import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useLang } from "../lib/LanguageContext";
import { BRAND } from "../lib/brand";
import { REGISTER_URL } from "../lib/config";
import { trackRegisterClick } from "../lib/analytics";
import UrgencyBadge from "../components/landing/UrgencyBadge";
import { Globe, Search, Sparkles, ArrowRight, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  id: {
    eyebrow: "KATALOG LIVE · UPDATE REAL-TIME",
    title: "Top Produk Grosir Margin Tinggi",
    subtitle: "Katalog live AsiaCommerce Hub. Selalu sinkron dengan database — produk baru muncul otomatis.",
    search: "Cari produk…",
    unlock: "Daftar untuk akses harga & order",
    empty: "Belum ada produk. Cek lagi nanti ya!",
    wholesale: "Grosir",
    retail: "Eceran",
    refTagline: "✨ Anda akses katalog ini lewat email lead magnet kami",
    chat: "Chat WhatsApp",
    catalogCount: (n) => `${n} produk aktif`,
  },
  en: {
    eyebrow: "LIVE CATALOG · REAL-TIME UPDATES",
    title: "Top High-Margin Wholesale Products",
    subtitle: "Live catalog from AsiaCommerce Hub — synced with our database. New products appear automatically.",
    search: "Search products…",
    unlock: "Sign up to access pricing & orders",
    empty: "No products yet. Check back soon!",
    wholesale: "Wholesale",
    retail: "Retail",
    refTagline: "✨ You accessed this catalog via our lead-magnet email",
    chat: "WhatsApp Chat",
    catalogCount: (n) => `${n} active products`,
  },
};

export default function Catalog() {
  const { lang, toggle } = useLang();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref");
  const c = COPY[lang] || COPY.id;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    document.title = `${c.title} · AsiaCommerce Hub`;
    axios.get(`${API}/products`).then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  }, [c.title]);

  const pickName = (p) => (lang === "id" ? p.name_id || p.name : p.name);
  const filtered = q
    ? items.filter((p) => `${p.name} ${p.name_id}`.toLowerCase().includes(q.toLowerCase()))
    : items;

  return (
    <div className="min-h-screen bg-cream" data-testid="catalog-page">
      {/* Header */}
      <header className="bg-white border-b border-soft sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src={BRAND.logoIcon} alt="AsiaCommerce" className="h-9 w-9 object-contain" />
            <span className="font-display font-extrabold text-lg text-ink hidden sm:inline">
              Asia<span className="text-brand-red">Commerce</span>
              <span className="ml-1 text-xs align-top bg-brand-orange text-white px-1.5 py-0.5 rounded font-bold">HUB</span>
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={toggle} className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-soft hover:border-brand-red hover:text-brand-red text-sm font-semibold" data-testid="catalog-lang-toggle">
              <Globe className="w-4 h-4" />
              {lang.toUpperCase()}
            </button>
            <a href={REGISTER_URL} rel="noopener" onClick={() => trackRegisterClick("catalog-header")}>
              <Button className="rounded-full bg-brand-red hover:bg-[#A60D25] text-white h-10 px-5 font-semibold" data-testid="catalog-register-btn">
                Daftar
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-subtle border border-brand-red/20 text-brand-red text-xs font-bold tracking-wider mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            {c.eyebrow}
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05]" data-testid="catalog-title">
            {c.title}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-ink-muted max-w-2xl">{c.subtitle}</p>
          {ref && (
            <p className="mt-3 text-xs font-semibold text-brand-orange" data-testid="catalog-ref-tag">{c.refTagline}</p>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={c.search}
                className="h-12 rounded-full pl-11 border-soft bg-white"
                data-testid="catalog-search"
              />
            </div>
            <div className="px-4 h-12 rounded-full bg-white border border-soft flex items-center gap-2 text-xs font-semibold text-ink-muted">
              {c.catalogCount(filtered.length)}
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {loading ? (
            <div className="text-center py-20 text-ink-muted">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-soft text-ink-muted" data-testid="catalog-empty">{c.empty}</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="catalog-grid">
              {filtered.map((p, idx) => (
                <div key={p.id} className="group rounded-2xl bg-white border border-soft overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid={`catalog-card-${idx+1}`}>
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#F3F3F3]">
                    <img src={p.img} alt={pickName(p)} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-brand-red text-white text-[10px] font-bold">{p.margin}</div>
                  </div>
                  <div className="p-3 sm:p-4">
                    <h3 className="font-semibold text-ink text-sm line-clamp-2 min-h-[40px]">{pickName(p)}</h3>
                    <div className="mt-1.5 mb-2"><UrgencyBadge id={p.id} lang={lang} /></div>
                    <div className="flex items-baseline justify-between">
                      <div>
                        <div className="text-[9px] font-semibold uppercase text-ink-muted">{c.wholesale}</div>
                        <div className="font-display font-bold text-ink text-sm">{p.wholesale}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[9px] font-semibold uppercase text-ink-muted">{c.retail}</div>
                        <div className="font-display font-bold text-brand-orange text-sm">{p.retail}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Sticky bottom CTA */}
      <div className="sticky bottom-0 bg-white border-t border-soft p-4 z-20">
        <div className="max-w-7xl mx-auto px-2 flex items-center justify-between gap-3">
          <p className="text-sm text-ink-muted hidden sm:block">{c.unlock}</p>
          <div className="flex items-center gap-2 ml-auto">
            <a href="https://wa.me/62881027917576" target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex">
              <Button variant="outline" className="rounded-full h-11 px-4 border-soft text-sm font-semibold">
                <MessageCircle className="w-4 h-4 mr-2 text-[#25D366]" />
                {c.chat}
              </Button>
            </a>
            <a href={REGISTER_URL} rel="noopener" onClick={() => trackRegisterClick("catalog-sticky")}>
              <Button className="rounded-full bg-brand-red hover:bg-[#A60D25] text-white h-11 px-5 font-semibold group" data-testid="catalog-sticky-btn">
                {lang === "id" ? "Daftar & Mulai Order" : "Sign Up & Order"}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
